import React from 'react';
import { Body } from '../Typography';
import styled, { css } from 'styled-components';
import type { BorderRadius } from '~/widget/theme/System/Default/border';

type Props = {
  dataTestId?: string;
  className?: string;
  children: React.ReactNode;
  href?: string;
  shouldRenderAsLink?: boolean;
  borderRadius?: BorderRadius;
  disabled?: boolean;
  onClick?: () => void;
};

const Button: React.FC<Props> = ({
  children,
  href,
  onClick,
  shouldRenderAsLink,
  dataTestId,
  disabled,
  borderRadius,
  ...props
}) => (
  <Container
    as={shouldRenderAsLink ? 'a' : 'button'}
    href={href}
    onClick={shouldRenderAsLink ? () => {} : onClick}
    data-testid={dataTestId}
    {...(shouldRenderAsLink ? { target: '_blank' } : {})}
    $disabled={disabled}
    $borderRadius={borderRadius}
    {...props}
  >
    <Body as="span" $margin={['xxxs', null, null, null]}>
      {children}
    </Body>
  </Container>
);

const outline = ({ theme }) => css`
  &:focus {
    box-shadow:
      0 0 0 2px #fff,
      0 0 0 4px ${theme.color('primary')};
    outline: 2px solid transparent;
  }
`;

const Container = styled.button<{
  $disabled?: boolean;
  $borderRadius?: BorderRadius;
}>(
  ({ theme, $disabled, $borderRadius = 'base' }) => css`
    cursor: ${$disabled ? 'no-drop' : 'pointer'};

    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    text-align: center;
    text-decoration: none;
    transition: all 0.3s ease;
    margin: 0;
    transform: scale(1);
    color: ${theme.color('primary', 'text')};
    border: ${theme.getSystem().border.width.s} solid ${theme.color('primary')};
    border-radius: ${$borderRadius
      ? theme.getSystem().border.radius[$borderRadius]
      : theme.getSystem().border.radius.base};

    background-color: ${theme.color('primary')};
    font-weight: ${theme.fw('semiBold')};
    padding: ${theme.space('m')};

    span {
      color: ${theme.color('primary', 'text')};
      font-weight: ${theme.fw('semiBold')};
    }

    &:hover,
    &:active {
      background-color: ${theme.color('primary', 'darker')};
    }

    &:active {
      transform: scale(0.975);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;

      &:hover,
      &:active {
        background-color: ${theme.color('primary', 'darker')};

        transform: scale(1);
      }
    }

    ${outline}
  `,
);

export default Button;
